import React, { useLayoutEffect } from "react";
import { Box, CssBaseline, Link, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Wordmark } from "../Logo";
import { Eyebrow, linkSx } from "../authShared";
import { CONTACT_EMAIL, LAST_UPDATED } from "./operator";

// ─────────────────────────────────────────────────────────────────────────────
// The reading layout shared by /terms and /privacy: one column on the paper
// background, a contents list, numbered sections. Public pages: no sign-in,
// nothing loaded from anywhere else.
// ─────────────────────────────────────────────────────────────────────────────

export const Mail = () => <Link href={`mailto:${CONTACT_EMAIL}`} sx={linkSx}>{CONTACT_EMAIL}</Link>;

export const Ext = ({ href, children }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer" sx={linkSx}>{children}</Link>
);

export const In = ({ to, children }) => <Link component={RouterLink} to={to} sx={linkSx}>{children}</Link>;

export const P = ({ children }) => (
  <Typography sx={{ fontSize: 16, lineHeight: 1.65, mb: 1.75 }}>{children}</Typography>
);

export const List = ({ items }) => (
  <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 2, "& li": { fontSize: 16, lineHeight: 1.6, mb: 0.75 } }}>
    {items.map((item, i) => <li key={i}>{item}</li>)}
  </Box>
);

/** One row of "what we keep": a card rather than a table, so it reads on a phone. */
export function DataCard({ title, what, why, kept }) {
  return (
    <Box sx={{ border: "1px solid var(--fn-line-l)", borderRadius: "12px", bgcolor: "var(--fn-white)", p: { xs: 2, sm: 2.5 }, mb: 1.5 }}>
      <Typography component="h3" sx={{ fontWeight: 700, fontSize: 16.5, mb: 1 }}>{title}</Typography>
      {[["What", what], ["Why", why], ["Kept", kept]].map(([label, text]) => (
        <Stack key={label} direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.25, sm: 1.5 }} sx={{ mb: 0.75 }}>
          <Typography sx={{ fontFamily: "var(--fn-mono)", fontSize: 12, letterSpacing: ".06em", textTransform: "uppercase",
            color: "var(--fn-teal-ink)", minWidth: 52, pt: "3px" }}>{label}</Typography>
          <Typography sx={{ fontSize: 15, lineHeight: 1.55, color: "var(--fn-navy)" }}>{text}</Typography>
        </Stack>
      ))}
    </Box>
  );
}

/**
 * sections: [{ id, title, body }]. The id is the anchor, so a section can be
 * linked to (e.g. /privacy#youtube from a Google review form).
 */
export default function LegalPage({ eyebrow, title, intro, sections }) {
  useLayoutEffect(() => {
    document.body.classList.add("fn-studio");
    const previous = document.title;
    document.title = `${title} · Fennira`;
    return () => { document.body.classList.remove("fn-studio"); document.title = previous; };
  }, [title]);

  return (
    <>
      <CssBaseline />
      <Box className="fn-auth" sx={{ minHeight: "100vh", bgcolor: "var(--fn-paper)", color: "var(--fn-navy)", fontFamily: "var(--fn-sans)",
        px: 2, py: { xs: 4, md: 7 } }}>
        <Box component="article" sx={{ maxWidth: 760, mx: "auto" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={2} sx={{ mb: { xs: 5, md: 7 } }}>
            <RouterLink to="/" aria-label="Fennira Studio"><Wordmark on="light" height={30} /></RouterLink>
            <Stack direction="row" spacing={2.5} component="nav" aria-label="Legal">
              <In to="/terms">Terms</In>
              <In to="/privacy">Privacy</In>
            </Stack>
          </Stack>

          <Eyebrow>{eyebrow}</Eyebrow>
          <Typography component="h1" sx={{ fontFamily: "var(--fn-display)", fontWeight: 800, fontSize: "clamp(36px, 6vw, 54px)",
            lineHeight: 1.02, letterSpacing: "-.028em", mt: 2.5, mb: 1.5, overflowWrap: "anywhere" }}>
            {title}
          </Typography>
          <Typography sx={{ fontFamily: "var(--fn-mono)", fontSize: 13, color: "var(--fn-muted-l)", mb: 4 }}>
            Last updated {LAST_UPDATED}
          </Typography>

          {intro}

          <Box component="nav" aria-label="Contents" sx={{ borderTop: "1px solid var(--fn-line-l)", borderBottom: "1px solid var(--fn-line-l)", py: 2.5, my: 4 }}>
            <Typography sx={{ fontFamily: "var(--fn-mono)", fontSize: 12, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--fn-muted-l)", mb: 1.5 }}>
              Contents
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 3, columns: { xs: 1, sm: 2 }, columnGap: 4, "& li": { fontSize: 15, mb: 0.75, breakInside: "avoid" } }}>
              {sections.map((s) => (
                <li key={s.id}><Link href={`#${s.id}`} sx={{ ...linkSx, fontWeight: 600 }}>{s.title}</Link></li>
              ))}
            </Box>
          </Box>

          {sections.map((s, i) => (
            <Box component="section" key={s.id} id={s.id} sx={{ scrollMarginTop: 24, mb: 5 }}>
              <Typography component="h2" sx={{ fontFamily: "var(--fn-display)", fontWeight: 800, fontSize: { xs: 24, sm: 28 },
                letterSpacing: "-.02em", lineHeight: 1.15, mb: 2 }}>
                <Box component="span" sx={{ color: "var(--fn-teal-ink)", mr: 1.25 }}>{i + 1}.</Box>{s.title}
              </Typography>
              {s.body}
            </Box>
          ))}

          <Box sx={{ borderTop: "1px solid var(--fn-line-l)", pt: 3, mt: 6 }}>
            <Typography sx={{ fontSize: 14.5, color: "var(--fn-muted-l)", lineHeight: 1.6 }}>
              Fennira is an independent project. Contact: <Mail />.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}
